interface Category {
  id: string | undefined;
  name: string | undefined;
}

interface Portifolio {
  id: string | undefined;
  name: string | undefined;
}

interface LastFetch {
  failed: boolean;
  lastFailure: number;
  reason: any;
}

interface Pagination {
  currentPageNumber: number;
  currentPageSize: number;
  isLoading: boolean;
  itemCount: number;
  pageCount: number;
  pageNumber: number;
  pageNumberInvalid: boolean;
  pageSize: number;
}

interface Place {
  category_id: string;
  id: string;
  name: string;
  keyword?: string;
  location?: { x: number; y: number } | null;
}

interface State {
  api: string;
  authorization: string;
  categories: Category[];
  portifolios: Portifolio[];
  loadingPortifolios: boolean;
  clientID: string;
  loadingCategories: boolean;
  loadingUsers: boolean;
  locale: string;
  location: LocationState;

  places: {
    category: Category;
    columns: object[];
    items: Place[];
    lastFetch: LastFetch;
    loadingPlaces: boolean;
    pagination: Pagination;
    selected: Place[];
    keyword?: string;
  };

  possiblePlaceStatuses: object;

  scheduling: {
    date: string;
    from: string;
    lastFetch: LastFetch;
    schedulingDone: boolean;
    user: User;
    sending: boolean;
  };

  sidebar: {
    active: boolean;
  };

  unvisitedPlaces: {
    category: Category;
    columns: object[];
    items: Place[];
    keyword?: string;
    lastFetch: LastFetch;
    loadingUnvisitedPlaces: boolean;
    pagination: Pagination;
    portifolio: Portifolio;
    selected: Place[];
    since: string;
    until: string;
    translations: object;
    isLoaded: boolean;
    hasSelectedAPlace: false;
    selectedPlace?: any;
    radius: number;
    coordinates: string;
  };

  unvisitedPlacesNearby: {
    columns: object[];
    coordinates: string;
    items: Place[];
    keyword?: string;
    lastFetch: LastFetch;
    loadingUnvisitedPlacesNearby: boolean;
    name: string;
    pagination: Pagination;
    portifolio: Portifolio;
    radius: number;
    selected: Place[];
    translations: object;
    isLoaded: boolean;
  };
  language: string;
  users: User[];
  intlObjectConfigUser: object;
}

interface User {
  id: string | undefined;
  name: string | undefined;
}

type TranslationsUnvisitedPlacesPT = typeof import("./translations/unvisited-places/pt.json");
type TranslationsUnvisitedPlacesEN = typeof import("./translations/unvisited-places/en.json");
type TranslationsUnvisitedPlacesES = typeof import("./translations/unvisited-places/es.json");

type TranslationsUnvisitedNearbyPlacesPT = typeof import("./translations/unvisited-places-nearby/pt.json");
type TranslationsUnvisitedNearbyPlacesEN = typeof import("./translations/unvisited-places-nearby/en.json");
type TranslationsUnvisitedNearbyPlacesES = typeof import("./translations/unvisited-places-nearby/es.json");

type TranslationsCommon = typeof import("./translations/common/pt.json");

interface UnvisitedPlacesTranslation {
  pt: TranslationsUnvisitedPlacesPT;
  en: TranslationsUnvisitedPlacesEN;
  es: TranslationsUnvisitedPlacesES;
}

interface UnvisitedPlacesNearbyTranslation {
  pt: TranslationsUnvisitedNearbyPlacesPT;
  en: TranslationsUnvisitedNearbyPlacesEN;
  es: TranslationsUnvisitedNearbyPlacesES;
}
interface Translations {
  unvisitedPlaces: UnvisitedPlacesTranslation;
  unvisitedPlacesNearby: UnvisitedPlacesNearbyTranslation;
  default: TranslationsCommon;
}

import { location as routerLocation, LocationState } from "@hyperapp/router";
import { format, subMonths } from "date-fns";

/* DD = dia do mês para versão do date-fns utilizada (1.30.1); */
const INITIAL_DATE = format(subMonths(new Date(), 1), "YYYY-MM-DD");
const FINAL_DATE = format(new Date(), "YYYY-MM-DD");

const category = { id: undefined, name: undefined };
const portifolio = { id: undefined, name: undefined };

const lastFetch = {
  failed: false,
  lastFailure: 0,
  reason: { message: "" },
};

const pagination = {
  currentPageNumber: 0,
  currentPageSize: 25,
  isLoading: false,
  itemCount: undefined,
  pageCount: undefined,
  pageNumber: 1,
  pageNumberInvalid: false,
  pageSize: 25,
};

const standardColumns = [
  { fixed: false, id: "address", selected: false, text: "Endereço" },
  { fixed: false, id: "category", selected: true, text: "Categoria" },
  { fixed: false, id: "cnpj_cpf", selected: false, text: "CNPJ ou CPF" },
  { fixed: false, id: "contacts", selected: false, text: "Contato" },
  // { fixed: true, id: "scheduling", selected: false, text: "Agendamento" },
  // { fixed: true, id: "near_places", selected: false, text: "Locais Próximos" },
  {
    fixed: false,
    id: "corporate_name",
    selected: false,
    text: "Razão Social",
  },
  { fixed: false, id: "id", selected: false, text: "ID" },
  { fixed: true, id: "name", selected: true, text: "Nome" },
  { fixed: false, id: "status", selected: false, text: "Status" },
  { fixed: false, id: "portifolios", selected: false, text: "Carteiras" },
  { fixed: false, id: "last_visit", selected: true, text: "Última Visita" },
];

const possiblePlaceStatuses = {
  "pt-BR": {
    not_positioned: "Não geoposicionado",
    valid: "Válido",
    positioned: "Geoposicionado",
    repositioned: "Reposicionado",
  },
};

export const state: State = {
  api: process.env.API_URL,
  authorization: `Bearer ${process.env.TOKEN}`,
  categories: [],
  clientID: process.env.CLIENT_ID || "",
  loadingCategories: false,
  loadingPortifolios: false,
  loadingUsers: false,
  locale: "pt-BR",
  location: routerLocation.state,
  places: {
    category,
    columns: standardColumns,
    items: [],
    lastFetch,
    loadingPlaces: false,
    pagination,
    selected: [],
  },
  portifolios: [],
  possiblePlaceStatuses,
  scheduling: {
    date: "",
    from: "",
    lastFetch,
    schedulingDone: false,
    user: { id: "", name: "" },
    sending: false,
  },
  sidebar: {
    active: false,
  },
  unvisitedPlaces: {
    category,
    columns: standardColumns,
    items: [],
    lastFetch,
    loadingUnvisitedPlaces: false,
    pagination,
    portifolio,
    selected: [],
    since: process.env.UNVISITED_PLACES_SINCE || INITIAL_DATE,
    until: process.env.UNVISITED_PLACES_SINCE || FINAL_DATE,
    translations: {},
    isLoaded: false,
    hasSelectedAPlace: false,
    radius: 1000,
    coordinates: "0,0",
  },
  unvisitedPlacesNearby: {
    columns: standardColumns,
    coordinates: "0,0",
    items: [],
    lastFetch,
    loadingUnvisitedPlacesNearby: false,
    name: "",
    pagination,
    portifolio: { id: undefined, name: undefined },
    radius: 1000,
    selected: [],
    translations: {},
    isLoaded: false,
  },
  language: "PT",
  users: [],
  intlObjectConfigUser: {},
};
